import { Link } from 'react-router-dom';
import { Button, Card, Coin } from '@/components/ui';
import ServiceIcon, { categoryTint } from '@/components/companion/ServiceIcon';
import type { ServiceCategory } from '@/lib/data/services';
import { rupees } from '@/lib/data/wallet';

interface AuthPromptModalProps {
  category: ServiceCategory | null;
  onClose: () => void;
}

export function AuthPromptModal({ category, onClose }: AuthPromptModalProps) {
  if (!category) return null;
  const tint = categoryTint(category.hue);

  const redirectParam = encodeURIComponent(`/services/${category.slug}`);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm transition-opacity duration-200"
      onClick={onClose}
    >
      <Card
        className="animate-rise relative w-full max-w-md border border-[var(--brand)]/20 bg-[var(--surface)] p-6 shadow-2xl shadow-[var(--brand)]/10 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-2)] hover:text-white"
          aria-label="Close"
        >
          ✕
        </button>

        <div className="flex items-center gap-3">
          <span
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl"
            style={tint}
          >
            <ServiceIcon iconKey={category.iconKey} size={24} />
          </span>
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--brand)]">
              Explore Companions
            </span>
            <h2 className="text-xl font-bold text-white">{category.name}</h2>
          </div>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-[var(--ink-2)]">
          Join MakeMyPartner to browse verified companions offering <strong>{category.name}</strong>,
          view photo galleries, check real-time availability, and book verified sessions.
        </p>

        <div className="mt-6 flex flex-col gap-3">
          <Link
            to={`/signup?redirect=${redirectParam}`}
            className="btn-grad flex h-11 w-full items-center justify-center rounded-xl font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
            onClick={onClose}
          >
            Create free account
          </Link>
          <Link
            to={`/login?redirect=${redirectParam}`}
            className="flex h-11 w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 font-semibold text-white transition-colors hover:bg-white/10"
            onClick={onClose}
          >
            Sign in
          </Link>
        </div>

        <p className="mt-4 text-center text-xs text-[var(--ink-2)]">
          Sign up takes less than 30 seconds.
        </p>
      </Card>
    </div>
  );
}

interface MembershipPromptModalProps {
  category: ServiceCategory | null;
  userFeePaise: number;
  onClose: () => void;
}

export function MembershipPromptModal({
  category,
  userFeePaise,
  onClose,
}: MembershipPromptModalProps) {
  if (!category) return null;
  const tint = categoryTint(category.hue);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm transition-opacity duration-200"
      onClick={onClose}
    >
      <Card
        className="animate-rise relative w-full max-w-md border border-[var(--marigold)]/30 bg-[var(--surface)] p-6 shadow-2xl shadow-[var(--marigold)]/10 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-[var(--ink-2)] transition-colors hover:bg-[var(--surface-2)] hover:text-white"
          aria-label="Close"
        >
          ✕
        </button>

        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--marigold-soft)] text-[var(--marigold)]">
          <Coin />
        </div>

        <h2 className="text-center text-2xl font-bold text-white">Unlock Full Directory Access</h2>

        <p className="mt-2 text-center text-sm text-[var(--ink-2)]">
          A one-time fee of <strong className="text-white">{rupees(userFeePaise)}</strong> unlocks
          all verified companions for <strong>{category.name}</strong> and the entire platform.
        </p>

        <div
          className="my-5 flex items-center gap-3 rounded-xl border border-white/10 p-3"
          style={{ background: 'var(--surface-2)' }}
        >
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={tint}
          >
            <ServiceIcon iconKey={category.iconKey} size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{category.name}</p>
            <p className="text-xs text-[var(--ink-2)]">ID & Video Verified Companions</p>
          </div>
        </div>

        <ul className="mb-6 flex flex-col gap-2 text-sm text-[var(--ink-2)]">
          {[
            'Lifetime directory browsing access',
            'Full profile photos, bio, rates & availability',
            'Direct chat, voice, video & in-person booking',
          ].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <span className="text-[var(--verified)]">✓</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-3">
          <Button to="/wallet" full onClick={onClose}>
            Pay {rupees(userFeePaise)} once in Wallet
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="text-center text-xs text-[var(--ink-2)] hover:text-white"
          >
            Maybe later
          </button>
        </div>
      </Card>
    </div>
  );
}
